import { Directive, HostListener, Input, inject } from '@angular/core';
import { Router } from '@angular/router';

/**
 * Hace que toda la fila de una `<tr class="ks-table ...">` navegue al
 * detalle, SIN sustituir el enlace real que ya vive sobre el nombre: son
 * mecanismos complementarios. El enlace da acceso por teclado y "abrir en
 * pestaña nueva"; el clic en la fila es solo una comodidad extra para
 * ratón/táctil.
 *
 * Implementado con `Router.navigate` en un manejador de clic, NO con el
 * truco del enlace extendido (`::after` cubriendo la fila): `position:
 * relative` sobre un `<tr>` es irregular entre navegadores, y un overlay a
 * sangre impediría seleccionar texto de la fila (p. ej. copiar un código).
 *
 * El clic se ignora si:
 * - Viene de un control interactivo (enlace, botón, input…) o de algo dentro
 *   de ellos — si no, pulsar "Editar"/"Desactivar" navegaría además.
 * - Hay texto seleccionado — un arrastre para copiar no debe navegar.
 * - Trae un modificador (Ctrl/Cmd/Shift/Alt) o no es el botón principal del
 *   ratón — son gestos de "abrir en otra pestaña/ventana"; en ese caso no
 *   navegamos en la pestaña actual, que sería justo lo contrario.
 *
 * A PROPÓSITO no pone `role="button"` ni `tabindex` en el `<tr>`: rompería
 * la semántica de la tabla y duplicaría el foco. El enlace de la celda sigue
 * siendo el ÚNICO camino por teclado; este directive es solo un atajo de
 * ratón.
 *
 * `appRowLink` acepta `null` para filas SIN detalle propio (p. ej. un
 * usuario de Firebase que aún no tiene registro en la base de datos): con
 * `null` el directive no navega y tampoco pinta `cursor: pointer`, así el
 * puntero no promete un clic que no hace nada. Se resuelve así, en vez de
 * quitar el atributo con `@if`/`@else` sobre toda la fila, para no duplicar
 * el marcado de la fila completa solo por esto.
 */
@Directive({
  selector: '[appRowLink]',
  standalone: true,
  host: {
    '[class.ks-row-link]': '!!appRowLink',
  },
})
export class RowLinkDirective {
  private readonly router = inject(Router);

  /**
   * Destino de la navegación: mismos "commands" que acepta `[routerLink]`.
   * `null`/`undefined` desactiva la fila (ver comentario de la clase).
   */
  @Input() appRowLink: unknown[] | string | null | undefined = null;

  @HostListener('click', ['$event'])
  protected onClick(event: MouseEvent): void {
    if (!this.appRowLink) return;

    if (event.button !== 0 || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) {
      return;
    }

    const target = event.target as HTMLElement | null;
    if (target?.closest('a, button, input, select, textarea, [role="button"]')) {
      return;
    }

    const selection = document.getSelection();
    if (selection && !selection.isCollapsed) {
      return;
    }

    const commands = Array.isArray(this.appRowLink) ? this.appRowLink : [this.appRowLink];
    void this.router.navigate(commands);
  }
}
